import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { DomainManagement } from '../components/DomainManagement';
import { PricingPlans } from '../components/PricingPlans';
import api from '../api/axios';
import { toast } from 'sonner';
import { Building2, Check, Globe, Leaf, Package, Rocket, Sparkles, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const projectSettingsTranslations = {
  tr: {
    planManagement: 'Plan Yönetimi',
    projectSettings: 'Proje Ayarları',
    manageProject: 'Proje ayarlarınızı ve widget domainlerini yönetin',
    general: 'Genel',
    generalSettings: 'Genel Ayarlar',
    generalDescription: 'Proje adı ve temel ayarlar',
    projectName: 'Proje Adı',
    projectNamePlaceholder: 'Örn: Web Uygulamam',
    projectSlug: 'Proje Slug',
    projectSlugDescription: 'URL-dostu tanımlayıcı',
    description: 'Açıklama (İsteğe Bağlı)',
    descriptionPlaceholder: 'Proje hakkında kısa bilgi...',
    widgetSettings: 'Domain',
    pricing: 'Hesap Planı',
    accountPlan: 'Hesap Planı',
    accountPlanDescription: 'Hesabınızın abonelik planını yönetin. Bu plan tüm projeleriniz için geçerlidir.',
    planFree: 'Ücretsiz',
    planStarter: 'Başlangıç',
    planProfessional: 'Profesyonel',
    planEnterprise: 'Kurumsal',
    featureNoPrioritySupport: 'Öncelikli destek yok',
    featurePrioritySupport: 'Öncelikli destek',
    featureNoAnalytics: 'Analitik verileri yok',
    featureAnalytics: 'Analitik verileri var',
    unlimited: 'Sınırsız',
    save: 'Kaydet',
    saving: 'Kaydediliyor...',
    saved: 'Başarıyla kaydedildi',
    saveFailed: 'Kaydetme başarısız oldu',
    loading: 'Yükleniyor...',
    projectNameRequired: 'Proje adı gerekli',
    projectNameMinLength: 'Proje adı en az 3 karakter olmalıdır',
    currentPlan: 'Mevcut Plan',
    getStarted: 'Hemen Başla',
    upgrade: 'Yükselt',
    downgrade: 'Düşür',
    contactUs: 'İletişime Geç',
    updating: 'Güncelleniyor...',
    popular: 'POPÜLER',
    perMonth: '/ay',
    perYear: '/yıl',
    monthly: 'Aylık',
    yearly: 'Yıllık',
    yearlySavingsBadge: '2 ay tasarruf',
    planUpgraded: 'Plan başarıyla güncellendi!',
    planUpgradeFailed: 'Plan güncellemesi başarısız oldu',
    upgradeTitle: 'Plan Yükseltme',
    upgradeDescription: 'Planınızı yükselterek daha fazla özelliğe erişebilirsiniz.',
    currentPlanLabel: 'Mevcut Plan:',
    newPlanLabel: 'Yeni Plan:',
    priceDifference: 'Bugün Ödenecek Fark (Kalan Süre İçin):',
    totalCharge: 'Toplam Tahsilat:',
    cancel: 'İptal Et',
    continuePayment: 'Ödemeye Devam Et',
    loadingPrice: 'Fiyat yükleniyor...',
    upgradeFailed: 'Plan yükseltme başarısız oldu',
    firstPaymentOnly: '* Sadece ilk ödeme. Daha sonra €{price}',
    downgradeTitle: 'Plan Düşürme',
    downgradeDescription: 'Plan değişikliğiniz fatura dönemi sonunda gerçekleşecektir.',
    downgradeWarning: 'Fatura dönemi bitimine kadar ({date}) mevcut plan özelliklerinizi kullanmaya devam edebilirsiniz.',
    confirmDowngrade: 'Değişikliği Onayla',
    pendingDowngradeTitle: 'Plan Değişikliği Zaten Bekleniyor',
    pendingDowngradeMessage: '{plan} paketine geçiş işleminiz {date} tarihinde gerçekleşecek şekilde zaten planlanmıştır. Lütfen işlemin tamamlanmasını bekleyin.',
    ok: 'Tamam',
    projects: 'Proje',
    domains: 'Domain',
    activeProjects: 'Aktif Projeler',
    activeProjectsDescription: 'Planınıza göre etkin olabilecek proje sayısını yönetin',
    maxActiveProjects: 'Maksimum Aktif Proje:',
    activeProjectsCount: 'Aktif Proje Sayısı:',
    totalProjects: 'Toplam Proje:',
    projectStatus: 'Durum',
    activate: 'Etkinleştir',
    deactivate: 'Devre Dışı Bırak',
    active: 'Aktif',
    inactive: 'Devre Dışı',
    noProjects: 'Henüz proje yok',
    activatingProject: 'Etkinleştiriliyor...',
    deactivatingProject: 'Devre dışı bırakılıyor...',
    projectActivated: 'Proje başarıyla etkinleştirildi',
    projectDeactivated: 'Proje başarıyla devre dışı bırakıldı',
    activationFailed: 'Etkinleştirme başarısız oldu',
    deactivationFailed: 'Devre dışı bırakma başarısız oldu',
    maxActiveProjectsReached: 'En fazla aktif proje sayısına ulaştınız',
  },
  en: {
    perYear: '/year',
    monthly: 'Monthly',
    yearly: 'Yearly',
    yearlySavingsBadge: 'Save 2 months',
    planManagement: 'Plan Management',
    projectSettings: 'Project Settings',
    manageProject: 'Manage your project settings and widget domains',
    general: 'General',
    generalSettings: 'General Settings',
    generalDescription: 'Project name and basic settings',
    projectName: 'Project Name',
    projectNamePlaceholder: 'e.g., My Web App',
    projectSlug: 'Project Slug',
    projectSlugDescription: 'URL-friendly identifier',
    description: 'Description (Optional)',
    descriptionPlaceholder: 'Brief information about the project...',
    widgetSettings: 'Domain',
    pricing: 'Account Plan',
    accountPlan: 'Account Plan',
    accountPlanDescription: 'Manage your account\'s subscription plan. This plan applies to all your projects.',
    planFree: 'Free',
    planStarter: 'Starter',
    planProfessional: 'Professional',
    planEnterprise: 'Enterprise',
    featureNoPrioritySupport: 'No priority support',
    featurePrioritySupport: 'Priority support',
    featureNoAnalytics: 'No analytics',
    featureAnalytics: 'Analytics included',
    unlimited: 'Unlimited',
    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved successfully',
    saveFailed: 'Failed to save',
    loading: 'Loading...',
    projectNameRequired: 'Project name is required',
    projectNameMinLength: 'Project name must be at least 3 characters',
    currentPlan: 'Current Plan',
    getStarted: 'Get Started',
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    contactUs: 'Contact Us',
    updating: 'Updating...',
    popular: 'POPULAR',
    perMonth: '/mo',
    planUpgraded: 'Plan upgraded successfully!',
    planUpgradeFailed: 'Plan upgrade failed',
    upgradeTitle: 'Upgrade Plan',
    upgradeDescription: 'Upgrade your plan to access more features.',
    currentPlanLabel: 'Current Plan:',
    newPlanLabel: 'New Plan:',
    priceDifference: 'Prorated Difference (Due Today):',
    totalCharge: 'Total Due Today:',
    cancel: 'Cancel',
    continuePayment: 'Continue to Payment',
    loadingPrice: 'Loading price...',
    upgradeFailed: 'Plan upgrade failed',
    firstPaymentOnly: '* First payment only. Then €{price}',
    projects: 'Projects',
    domains: 'Domains',
    features: 'Features',
    downgradeTitle: 'Downgrade Plan',
    downgradeDescription: 'Your plan change will take effect at the end of the billing cycle.',
    downgradeWarning: 'You can keep using the current plan features until the billing period ends on {date}.',
    confirmDowngrade: 'Confirm Change',
    activeProjects: 'Active Projects',
    activeProjectsDescription: 'Manage how many projects can be active based on your plan',
    maxActiveProjects: 'Maximum Active Projects:',
    activeProjectsCount: 'Active Projects Count:',
    totalProjects: 'Total Projects:',
    projectStatus: 'Status',
    activate: 'Activate',
    deactivate: 'Deactivate',
    active: 'Active',
    inactive: 'Inactive',
    noProjects: 'No projects yet',
    activatingProject: 'Activating...',
    deactivatingProject: 'Deactivating...',
    projectActivated: 'Project activated successfully',
    projectDeactivated: 'Project deactivated successfully',
    activationFailed: 'Activation failed',
    deactivationFailed: 'Deactivation failed',
    maxActiveProjectsReached: 'You have reached the maximum number of active projects',
  },
  de: {
    perYear: '/Jahr',
    monthly: 'Monatlich',
    yearly: 'Jährlich',
    yearlySavingsBadge: '2 Monate sparen',
    planManagement: 'Planverwaltung',
    projectSettings: 'Projekteinstellungen',
    manageProject: 'Verwalten Sie Ihre Projekteinstellungen und Widget-Domänen',
    general: 'Allgemein',
    generalSettings: 'Allgemeine Einstellungen',
    generalDescription: 'Projektname und Grundeinstellungen',
    projectName: 'Projektname',
    projectNamePlaceholder: 'z.B. Meine Web-App',
    projectSlug: 'Projekt-Slug',
    projectSlugDescription: 'URL-freundliche Kennung',
    description: 'Beschreibung (Optional)',
    descriptionPlaceholder: 'Kurze Informationen zum Projekt...',
    widgetSettings: 'Domain',
    pricing: 'Kontoplan',
    accountPlan: 'Kontoplan',
    accountPlanDescription: 'Verwalten Sie Ihren Abonnementplan. Dieser Plan gilt für alle Ihre Projekte.',
    planFree: 'Kostenlos',
    planStarter: 'Starter',
    planProfessional: 'Professionell',
    planEnterprise: 'Enterprise',
    featureNoPrioritySupport: 'Kein Prioritäts-Support',
    featurePrioritySupport: 'Prioritäts-Support',
    featureNoAnalytics: 'Keine Analytics',
    featureAnalytics: 'Analytics enthalten',
    unlimited: 'Unbegrenzt',
    save: 'Speichern',
    saving: 'Speichern...',
    saved: 'Erfolgreich gespeichert',
    saveFailed: 'Speichern fehlgeschlagen',
    loading: 'Wird geladen...',
    projectNameRequired: 'Projektname ist erforderlich',
    projectNameMinLength: 'Projektname muss mindestens 3 Zeichen lang sein',
    currentPlan: 'Aktueller Plan',
    getStarted: 'Jetzt starten',
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    contactUs: 'Kontaktieren Sie uns',
    updating: 'Wird aktualisiert...',
    popular: 'BELIEBT',
    perMonth: '/Monat',
    planUpgraded: 'Plan erfolgreich aktualisiert!',
    planUpgradeFailed: 'Plan-Upgrade fehlgeschlagen',
    upgradeTitle: 'Plan-Upgrade',
    upgradeDescription: 'Aktualisieren Sie Ihren Plan, um auf weitere Funktionen zuzugreifen.',
    currentPlanLabel: 'Aktueller Plan:',
    newPlanLabel: 'Neuer Plan:',
    priceDifference: 'Anteilige Differenz (Heute fällig):',
    totalCharge: 'Heute fällig:',
    cancel: 'Abbrechen',
    continuePayment: 'Zur Zahlung fortfahren',
    loadingPrice: 'Preis wird geladen...',
    upgradeFailed: 'Plan-Upgrade fehlgeschlagen',
    firstPaymentOnly: '* Nur erste Zahlung. Dann €{price}',
    projects: 'Projekte',
    domains: 'Domänen',
    features: 'Funktionen',
    downgradeTitle: 'Plan Downgrade',
    downgradeDescription: 'Die Planänderung wird am Ende des Abrechnungszeitraums wirksam.',
    downgradeWarning: 'Sie können die aktuellen Funktionen bis zum Ende des Abrechnungszeitraums am {date} nutzen.',
    confirmDowngrade: 'Änderung bestätigen',
    pendingDowngradeTitle: 'Änderung bereits geplant',
    pendingDowngradeMessage: 'Es ist bereits eine Planänderung auf {plan} am {date} geplant. Bitte warten Sie, bis diese Änderung wirksam wird.',
    ok: 'OK',
    activeProjects: 'Aktive Projekte',
    activeProjectsDescription: 'Verwalten Sie, wie viele Projekte basierend auf Ihrem Plan aktiv sein können',
    maxActiveProjects: 'Maximale aktive Projekte:',
    activeProjectsCount: 'Anzahl aktiver Projekte:',
    totalProjects: 'Gesamtprojekte:',
    projectStatus: 'Status',
    activate: 'Aktivieren',
    deactivate: 'Deaktivieren',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    noProjects: 'Noch keine Projekte',
    activatingProject: 'Wird aktiviert...',
    deactivatingProject: 'Wird deaktiviert...',
    projectActivated: 'Projekt erfolgreich aktiviert',
    projectDeactivated: 'Projekt erfolgreich deaktiviert',
    activationFailed: 'Aktivierung fehlgeschlagen',
    deactivationFailed: 'Deaktivierung fehlgeschlagen',
    maxActiveProjectsReached: 'Sie haben die maximale Anzahl aktiver Projekte erreicht',
  },
  es: {
    perYear: '/año',
    monthly: 'Mensual',
    yearly: 'Anual',
    yearlySavingsBadge: 'Ahorra 2 meses',
    planManagement: 'Gestión de Planes',
    projectSettings: 'Configuración del Proyecto',
    manageProject: 'Administre la configuración de su proyecto y los dominios de widget',
    general: 'General',
    generalSettings: 'Configuración General',
    generalDescription: 'Nombre del proyecto y configuración básica',
    projectName: 'Nombre del Proyecto',
    projectNamePlaceholder: 'p.ej., Mi Aplicación Web',
    projectSlug: 'Slug del Proyecto',
    projectSlugDescription: 'Identificador amigable con URL',
    description: 'Descripción (Opcional)',
    descriptionPlaceholder: 'Información breve sobre el proyecto...',
    widgetSettings: 'Dominio',
    pricing: 'Plan de Cuenta',
    accountPlan: 'Plan de Cuenta',
    accountPlanDescription: 'Administre su plan de suscripción. Este plan se aplica a todos sus proyectos.',
    planFree: 'Gratis',
    planStarter: 'Starter',
    planProfessional: 'Profesional',
    planEnterprise: 'Empresarial',
    featureNoPrioritySupport: 'Sin soporte prioritario',
    featurePrioritySupport: 'Soporte prioritario',
    featureNoAnalytics: 'Sin analíticas',
    featureAnalytics: 'Analíticas incluidas',
    unlimited: 'Ilimitado',
    save: 'Guardar',
    saving: 'Guardando...',
    saved: 'Guardado exitosamente',
    saveFailed: 'Error al guardar',
    loading: 'Cargando...',
    projectNameRequired: 'El nombre del proyecto es obligatorio',
    projectNameMinLength: 'El nombre del proyecto debe tener al menos 3 caracteres',
    currentPlan: 'Plan Actual',
    getStarted: 'Comenzar',
    upgrade: 'Actualizar',
    downgrade: 'Reducir',
    contactUs: 'Contáctenos',
    updating: 'Actualizando...',
    popular: 'POPULAR',
    perMonth: '/mes',
    planUpgraded: '¡Plan actualizado exitosamente!',
    planUpgradeFailed: 'Error en la actualización del plan',
    upgradeTitle: 'Actualizar Plan',
    upgradeDescription: 'Actualice su plan para acceder a más funciones.',
    currentPlanLabel: 'Plan Actual:',
    newPlanLabel: 'Nuevo Plan:',
    priceDifference: 'Diferencia prorrateada (A pagar hoy):',
    totalCharge: 'Total a pagar hoy:',
    cancel: 'Cancelar',
    continuePayment: 'Continuar al Pago',
    loadingPrice: 'Cargando precio...',
    upgradeFailed: 'Error en la actualización del plan',
    firstPaymentOnly: '* Solo primer pago. Luego €{price}',
    projects: 'Proyectos',
    domains: 'Dominios',
    features: 'Características',
    downgradeTitle: 'Bajar de Plan',
    downgradeDescription: 'El cambio de plan se aplicará al final del ciclo de facturación.',
    downgradeWarning: 'Puede seguir usando las funciones actuales hasta que el período de facturación termine el {date}.',
    confirmDowngrade: 'Confirmar Cambio',
    pendingDowngradeTitle: 'Cambio ya programado',
    pendingDowngradeMessage: 'Ya tiene un cambio de plan programado para {plan} el {date}. Por favor espere a que este cambio surta efecto.',
    ok: 'OK',
    activeProjects: 'Proyectos Activos',
    activeProjectsDescription: 'Administre cuántos proyectos pueden estar activos según su plan',
    maxActiveProjects: 'Máximo de proyectos activos:',
    activeProjectsCount: 'Cantidad de proyectos activos:',
    totalProjects: 'Total de proyectos:',
    projectStatus: 'Estado',
    activate: 'Activar',
    deactivate: 'Desactivar',
    active: 'Activo',
    inactive: 'Inactivo',
    noProjects: 'Sin proyectos aún',
    activatingProject: 'Activando...',
    deactivatingProject: 'Desactivando...',
    projectActivated: 'Proyecto activado exitosamente',
    projectDeactivated: 'Proyecto desactivado exitosamente',
    activationFailed: 'Error al activar',
    deactivationFailed: 'Error al desactivar',
    maxActiveProjectsReached: 'Ha alcanzado el número máximo de proyectos activos',
  },
  fr: {
    perYear: '/an',
    monthly: 'Mensuel',
    yearly: 'Annuel',
    yearlySavingsBadge: 'Économisez 2 mois',
    planManagement: 'Gestion des Plans',
    projectSettings: 'Paramètres du Projet',
    manageProject: 'Gérez vos paramètres de projet et vos domaines de widget',
    general: 'Général',
    generalSettings: 'Paramètres Généraux',
    generalDescription: 'Nom du projet et paramètres de base',
    projectName: 'Nom du Projet',
    projectNamePlaceholder: 'ex: Ma Webapp',
    projectSlug: 'Slug du Projet',
    projectSlugDescription: 'Identifiant convivial pour les URL',
    description: 'Description (Optionnel)',
    descriptionPlaceholder: 'Brèves informations sur le projet...',
    widgetSettings: 'Domaine',
    pricing: 'Plan du Compte',
    accountPlan: 'Plan du Compte',
    accountPlanDescription: 'Gérez votre plan d\'abonnement. Ce plan s\'applique à tous vos projets.',
    planFree: 'Gratuit',
    planStarter: 'Starter',
    planProfessional: 'Professionnel',
    planEnterprise: 'Entreprise',
    featureNoPrioritySupport: 'Pas de support prioritaire',
    featurePrioritySupport: 'Support prioritaire',
    featureNoAnalytics: 'Pas d’analytique',
    featureAnalytics: 'Analytique incluse',
    unlimited: 'Illimité',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    saved: 'Enregistré avec succès',
    saveFailed: 'Impossible d\'enregistrer',
    loading: 'Chargement...',
    projectNameRequired: 'Le nom du projet est requis',
    projectNameMinLength: 'Le nom du projet doit contenir au moins 3 caractères',
    currentPlan: 'Plan Actuel',
    getStarted: 'Commencer',
    upgrade: 'Mettre à niveau',
    downgrade: 'Réduire',
    contactUs: 'Nous Contacter',
    updating: 'Mise à jour...',
    popular: 'POPULAIRE',
    perMonth: '/mois',
    planUpgraded: 'Plan mis à niveau avec succès!',
    planUpgradeFailed: 'Échec de la mise à niveau du plan',
    upgradeTitle: 'Mettre à niveau le plan',
    upgradeDescription: 'Mettez à niveau votre plan pour accéder à plus de fonctionnalités.',
    currentPlanLabel: 'Plan actuel:',
    newPlanLabel: 'Nouveau plan:',
    priceDifference: 'Différence au prorata (Dû aujourd\'hui):',
    totalCharge: 'Total à payer aujourd\'hui:',
    cancel: 'Annuler',
    continuePayment: 'Continuer le paiement',
    loadingPrice: 'Chargement du prix...',
    upgradeFailed: 'Échec de la mise à niveau du plan',
    firstPaymentOnly: '* Paiement initial uniquement. Ensuite €{price}',
    projects: 'Projets',
    domains: 'Domaines',
    features: 'Caractéristiques',
    downgradeTitle: 'Rétrograder le Plan',
    downgradeDescription: 'Votre changement de plan prendra effet à la fin du cycle de facturation.',
    downgradeWarning: 'Vous pouvez continuer à utiliser les fonctionnalités du plan actuel jusqu\'à la fin de la période de facturation le {date}.',
    confirmDowngrade: 'Confirmer le Changement',
    pendingDowngradeTitle: 'Changement Déjà Programmé',
    pendingDowngradeMessage: 'Vous avez déjà un changement de plan programmé pour {plan} le {date}. Veuillez attendre que ce changement prenne effet.',
    ok: 'OK',
    activeProjects: 'Projets Actifs',
    activeProjectsDescription: 'Gérez le nombre de projets qui peuvent être actifs selon votre plan',
    maxActiveProjects: 'Nombre maximum de projets actifs:',
    activeProjectsCount: 'Nombre de projets actifs:',
    totalProjects: 'Nombre total de projets:',
    projectStatus: 'Statut',
    activate: 'Activer',
    deactivate: 'Désactiver',
    active: 'Actif',
    inactive: 'Inactif',
    noProjects: 'Aucun projet pour le moment',
    activatingProject: 'Activation en cours...',
    deactivatingProject: 'Désactivation en cours...',
    projectActivated: 'Projet activé avec succès',
    projectDeactivated: 'Projet désactivé avec succès',
    activationFailed: 'Échec de l\'activation',
    deactivationFailed: 'Échec de la désactivation',
    maxActiveProjectsReached: 'Vous avez atteint le nombre maximum de projets actifs',
  },
};

export const ProjectSettings = () => {
  const { activeProject, refreshProjects, loadProjects, user } = useAuth();
  const { language, t: tGlobal } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [projectData, setProjectData] = useState({
    name: '',
    slug: '',
    description: '',
  });

  const [originalProjectData, setOriginalProjectData] = useState({
    name: '',
    description: '',
  });

  const [accountPlan, setAccountPlan] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [upgradePricing, setUpgradePricing] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [isDowngrade, setIsDowngrade] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactFormData, setContactFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [stripePlans, setStripePlans] = useState({});
  const [billingInterval, setBillingInterval] = useState('month'); // month | year

  const t = projectSettingsTranslations[language] || projectSettingsTranslations.en;

  // Helper function to get translated plan names
  const getPlanName = (planKey) => {
    const planMap = {
      'free': t.planFree,
      'starter': t.planStarter,
      'premium': t.planProfessional,
      'enterprise': t.planEnterprise,
    };
    return planMap[planKey?.toLowerCase()] || planKey;
  };

  const allowedTabs = ['general', 'widget', 'pricing', 'active-projects'];
  const tabParam = searchParams.get('tab');
  const tabFromUrl = allowedTabs.includes(tabParam) ? tabParam : 'general';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  const planVisuals = {
    free: {
      Icon: Leaf,
      gradient: 'from-slate-50 to-slate-100',
      accentText: 'text-slate-800',
      iconWrap: 'bg-white/70 ring-1 ring-slate-200',
    },
    starter: {
      Icon: Rocket,
      gradient: 'from-cyan-50 to-sky-100',
      accentText: 'text-cyan-900',
      iconWrap: 'bg-white/70 ring-1 ring-cyan-200',
    },
    premium: {
      Icon: Sparkles,
      gradient: 'from-violet-50 to-fuchsia-100',
      accentText: 'text-violet-900',
      iconWrap: 'bg-white/70 ring-1 ring-violet-200',
    },
    enterprise: {
      Icon: Building2,
      gradient: 'from-amber-50 to-orange-100',
      accentText: 'text-amber-900',
      iconWrap: 'bg-white/70 ring-1 ring-amber-200',
    },
  };

  // Base plans (limits + localized feature text). Prices are updated from Stripe later.
  const basePlans = {
    free: {
      name: t.planFree || 'Free',
      price: '0',
      projects: 1,
      domains: 1,
      features: [
        { text: t.featureNoPrioritySupport || 'No priority support', included: false },
        { text: t.featureNoAnalytics || 'No analytics', included: false },
      ],
      buttonLabel: t.currentPlan,
      isPopular: false,
    },
    starter: {
      name: t.planStarter || 'Starter',
      price: '...',
      projects: 5,
      domains: 5,
      features: [
        { text: t.featurePrioritySupport || 'Priority support', included: true },
        { text: t.featureAnalytics || 'Analytics included', included: true },
      ],
      buttonLabel: t.getStarted,
      isPopular: true,
    },
    // Keep key as "premium" to match backend/stripe plan keys, but show "Professional" in UI.
    premium: {
      name: t.planProfessional || 'Professional',
      price: '...',
      projects: 30,
      domains: 30,
      features: [
        { text: t.featurePrioritySupport || 'Priority support', included: true },
        { text: t.featureAnalytics || 'Analytics included', included: true },
      ],
      buttonLabel: t.upgrade,
      isPopular: false,
    },
    enterprise: {
      name: t.planEnterprise || 'Enterprise',
      price: '...',
      projects: Infinity,
      domains: Infinity,
      features: [
        { text: t.featurePrioritySupport || 'Priority support', included: true },
        { text: t.featureAnalytics || 'Analytics included', included: true },
      ],
      buttonLabel: t.contactUs,
      isPopular: false,
    },
  };

  const [plans, setPlans] = useState(basePlans);

  useEffect(() => {
    // Fetch dynamic pricing from backend ONCE
    const fetchStripePlans = async () => {
      try {
        const response = await api.get('/stripe/plans');
        if (response.data) {
          setStripePlans(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch Stripe plans:", error);
      }
    };

    fetchStripePlans();
  }, []);

  // Update displayed prices whenever billingInterval OR stripePlans changes
  useEffect(() => {
    if (Object.keys(stripePlans).length === 0) return;

    setPlans(prevPlans => {
      const newPlans = { ...prevPlans };

      Object.keys(stripePlans).forEach(planKey => {
        if (newPlans[planKey]) {
          // Backend now returns prices per interval: { prices: { month: {price,currency}, year: {...} } }
          const prices = stripePlans[planKey]?.prices || {};
          const selected = prices[billingInterval] || prices.month || prices.year;
          if (selected?.price !== undefined) {
            newPlans[planKey] = {
              ...newPlans[planKey],
              price: selected.price.toString(),
              currency: selected.currency
            };
          }
        }
      });

      return newPlans;
    });
  }, [billingInterval, stripePlans]);

  // When language changes, refresh plan names/features but preserve fetched prices/currency.
  useEffect(() => {
    setPlans((prev) => {
      const next = { ...prev };
      Object.keys(basePlans).forEach((k) => {
        next[k] = {
          ...basePlans[k],
          // preserve dynamic pricing if it exists
          price: prev?.[k]?.price ?? basePlans[k].price,
          currency: prev?.[k]?.currency ?? basePlans[k].currency,
        };
      });
      return next;
    });
    // eslint-disable-next-line
  }, [language]);

  useEffect(() => {
    if (activeProject) {
      loadProjectSettings();
      loadAccountPlan();
    }
  }, [activeProject]);

  // Update billingInterval when accountPlan is loaded
  useEffect(() => {
    if (accountPlan?.billing_interval) {
      setBillingInterval(accountPlan.billing_interval);
    }
  }, [accountPlan]);

  // If URL has no `tab`, set it once (so refresh always has a stable tab param)
  useEffect(() => {
    if (!tabParam) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tabFromUrl);
      setSearchParams(next, { replace: true });
    }
  }, [tabParam, tabFromUrl, searchParams, setSearchParams]);

  // If user navigates via back/forward or URL is edited, reflect tab changes in UI
  useEffect(() => {
    if (tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
  }, [tabFromUrl, activeTab]);

  const handleTabChange = (nextTab) => {
    const safeTab = allowedTabs.includes(nextTab) ? nextTab : 'general';
    setActiveTab(safeTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', safeTab);
    setSearchParams(next, { replace: true });
  };

  // Check if returning from Stripe checkout success
  const sessionProcessedRef = React.useRef(false);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (sessionId && !sessionProcessedRef.current) {
      sessionProcessedRef.current = true;
      setIsProcessingCheckout(true); // Start blocking loading state

      // Verify and sync checkout session data from backend
      const syncCheckoutSession = async () => {
        try {
          const response = await api.get(`/stripe/session/${sessionId}`);
          if (response.data.success) {
            toast.success(t.planUpgraded || 'Plan upgraded successfully!');
            // Reload plan and projects
            await loadAccountPlan();
            if (loadProjects) {
              await loadProjects();
            }
          }
        } catch (error) {
          console.error('Failed to sync checkout session:', error);
          // Still try to reload plan even if sync fails
          await loadAccountPlan();
          if (loadProjects) {
            await loadProjects();
          }
        } finally {
          // Remove only session_id from URL (preserve tab and other params)
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('session_id');
            window.history.replaceState({}, document.title, url.pathname + url.search);
          } catch {
            // Fallback: keep behavior safe
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          setIsProcessingCheckout(false); // Release blocking loading state
        }
      };

      syncCheckoutSession();
    }
  }, []); // Empty dependency array - only run once on mount

  const loadProjectSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${activeProject.id}/settings`);
      const loadedData = {
        name: activeProject.name || '',
        slug: activeProject.slug || '',
        description: response.data.description || '',
      };
      setProjectData(loadedData);
      // Store original data for comparison
      setOriginalProjectData({
        name: activeProject.name || '',
        description: response.data.description || '',
      });
    } catch (error) {
      console.error('Failed to load project settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountPlan = async () => {
    try {
      const response = await api.get('/account/plan');
      setAccountPlan(response.data);
    } catch (error) {
      console.error('Failed to load account plan:', error);
      toast.error('Failed to load account plan information.');
    }
  };

  const handleSaveSettings = async () => {
    if (!projectData.name.trim()) {
      toast.error(t.projectNameRequired);
      return;
    }
    if (projectData.name.trim().length < 3) {
      toast.error(t.projectNameMinLength);
      return;
    }

    // Check if anything has changed
    const nameChanged = projectData.name.trim() !== originalProjectData.name.trim();
    const descriptionChanged = (projectData.description || '').trim() !== (originalProjectData.description || '').trim();

    if (!nameChanged && !descriptionChanged) {
      // No changes made, don't save
      return;
    }

    setSaving(true);
    try {
      await api.put(`/projects/${activeProject.id}/settings`, {
        name: projectData.name,
        description: projectData.description,
      });
      
      // Update original data after successful save
      setOriginalProjectData({
        name: projectData.name.trim(),
        description: projectData.description || '',
      });
      
      toast.success(t.saved);
      if (refreshProjects) {
        await refreshProjects();
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const handlePlanUpgradeClick = useCallback(async (planType) => {
    const currentPlan = accountPlan?.plan_type || 'free';
    const currentInterval = accountPlan?.billing_interval || 'month';

    // Check if user is already on the selected plan AND billing interval
    if (planType === currentPlan && billingInterval === currentInterval) {
      return;
    }

    // Enterprise: open contact modal (same SMTP as landing page contact form).
    if (planType === 'enterprise') {
      setContactFormData({
        name: user?.name || '',
        email: user?.email || '',
        subject: tGlobal.contactModalTitle || 'Enterprise plan',
        message: '',
      });
      setShowContactModal(true);
      return;
    }

    // Determine if downgrade
    // First check: if same plan type but different interval, compare prices
    // Second check: if different plan type, use hierarchy
    let isDowngradeAction = false;

    if (planType === currentPlan) {
      // Same plan type, different billing interval (monthly <-> yearly)
      // Determine downgrade by comparing prices
      const currentPlanDetails = plans[currentPlan];
      if (currentPlanDetails && currentPlanDetails.price !== '...') {
        // Plans state stores price in Dollars
        const currentPrice = parseFloat(currentPlanDetails.price) || 0;
        // Get new plan price
        const stripePlanData = stripePlans[planType];
        if (stripePlanData && stripePlanData.prices) {
          const newPriceData = stripePlanData.prices[billingInterval] || stripePlanData.prices.month;
          const newPrice = newPriceData?.price || 0;
          isDowngradeAction = newPrice < currentPrice;
        }
      }
    } else {
      // Different plan type - use hierarchy
      const planHierarchy = ['free', 'starter', 'premium', 'enterprise'];
      const currentIndex = planHierarchy.indexOf(currentPlan);
      const newIndex = planHierarchy.indexOf(planType);
      isDowngradeAction = newIndex < currentIndex;
    }

    setUpgradeTarget(planType);

    // Check for pending scheduled changes
    if (accountPlan.scheduled_change) {
      setShowPendingModal(true);
      return;
    }

    // Special handling for Free plan (Downgrade)
    if (planType === 'free') {
      const currentPlanDetails = plans[currentPlan];
      // plans state stores price in Dollars, mock pricing expects Cents
      const currentPriceCents = currentPlanDetails ? (parseFloat(currentPlanDetails.price) || 0) * 100 : 0;

      setUpgradePricing({
        current_plan: currentPlan,
        new_plan: 'free',
        current_price: currentPriceCents,
        new_price: 0,
        price_difference: 0,
        current_period_end: accountPlan.current_period_end
      });
      // Free plan downgrade
      setIsDowngrade(true);
      setShowUpgradeModal(true);
      return;
    }

    // Logic for Paid Plans
    setUpgradeLoading(true);
    setShowUpgradeModal(true); // Open immediately for loading state

    try {
      const response = await api.get('/stripe/upgrade-preview', {
        params: {
          current_plan: currentPlan,
          new_plan: planType,
          billing_interval: billingInterval,
        },
      });

      // Determine upgrade/downgrade based on price_difference from backend
      // price_difference > 0 = upgrade (user pays)
      // price_difference < 0 = downgrade (credit)
      const isDowngradeBasedOnPrice = response.data.price_difference < 0;
      setIsDowngrade(isDowngradeBasedOnPrice);

      setUpgradePricing(response.data);
    } catch (error) {
      console.error('Failed to load upgrade preview:', error);
      toast.error(t.upgradeFailed);
      setShowUpgradeModal(false); // Close on error
    } finally {
      setUpgradeLoading(false);
    }
  }, [accountPlan, billingInterval, plans, stripePlans, navigate, t, user, tGlobal]);

  const confirmUpgrade = async () => {
    setSaving(true);
    try {
      if (upgradeTarget === 'free') {
        // Handle Downgrade to Free
        const response = await api.put(`/account/plan`, {
          plan_type: 'free',
        });

        toast.success(response.data.message || 'Plan downgrade scheduled.');
        // Update accountPlan state with the response data (includes scheduled_change)
        setAccountPlan(response.data);
        if (loadProjects) {
          await loadProjects();
        }
        setShowUpgradeModal(false);
      } else {
        // Handle Upgrade/Change to Paid Plan via Stripe
        const response = await api.post('/stripe/checkout', {
          plan_type: upgradeTarget,
          billing_interval: billingInterval,
        });

        if (response.data?.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = response.data.checkout_url;
        } else {
          toast.error('Failed to create checkout session');
          setShowUpgradeModal(false); // Only close if not redirecting
        }
      }
    } catch (error) {
      console.error('Failed to change plan:', error);
      const errorMessage = error.response?.data?.detail || t.planUpgradeFailed;
      toast.error(errorMessage);
      setShowUpgradeModal(false);
    } finally {
      setSaving(false);
      // Don't close modal immediately if redirecting, but effectively we are navigating away.
      // If error or free plan, we closed it above or catch block.
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactFormData.name?.trim() || !contactFormData.email?.trim() || !contactFormData.subject?.trim() || !contactFormData.message?.trim()) {
      toast.error(tGlobal.allFieldsRequired || 'Please fill all fields');
      return;
    }
    setContactLoading(true);
    try {
      await api.post('/contact', {
        name: contactFormData.name.trim(),
        email: contactFormData.email.trim(),
        subject: contactFormData.subject.trim(),
        message: contactFormData.message.trim(),
      });
      toast.success(tGlobal.messageSent || 'Message sent');
      setShowContactModal(false);
      setContactFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || tGlobal.messageFailed || 'Failed to send');
    } finally {
      setContactLoading(false);
    }
  };

  // Plans are now in state 'plans'

  if (!activeProject || loading || !accountPlan || isProcessingCheckout) {
    return (
      <>
        <div className="p-8 flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mb-4"></div>
          <p className="text-gray-600">{isProcessingCheckout ? t.updating : t.loading}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Project Settings - FeedPanels</title>
        <meta name="description" content="Configure your FeedPanels project: domains, widget, pricing plan, and active projects. Manage project settings in one place." />
        <link rel="canonical" href="https://feedpanels.com/dashboard/project-settings" />
      </Helmet>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">{t.projectSettings}</h1>
          <p className="text-sm md:text-base text-gray-600">{t.manageProject}</p>
        </div>

        <Tabs value={activeTab} onValueChange={showUpgradeModal ? undefined : handleTabChange} className="space-y-6">
          <TabsList className={showUpgradeModal ? 'pointer-events-none opacity-50' : ''}>
            <TabsTrigger value="general">{t.general}</TabsTrigger>
            <TabsTrigger value="widget">{t.widgetSettings}</TabsTrigger>
            <TabsTrigger value="active-projects">{t.activeProjects}</TabsTrigger>
            <TabsTrigger value="pricing">{t.pricing}</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 md:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl">{t.generalSettings}</CardTitle>
                <CardDescription className="text-sm md:text-base">{t.generalDescription}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-6">
                <div>
                  <Label htmlFor="projectName" className="text-sm md:text-base">{t.projectName}</Label>
                  <Input id="projectName" value={projectData.name} onChange={(e) => setProjectData({ ...projectData, name: e.target.value })} placeholder={t.projectNamePlaceholder} className="mt-2 text-sm md:text-base" disabled={saving} />
                </div>
                <div>
                  <Label htmlFor="projectSlug" className="text-sm md:text-base">{t.projectSlug}</Label>
                  <Input id="projectSlug" value={projectData.slug} disabled className="mt-2 bg-gray-50 dark:bg-gray-800 cursor-not-allowed text-sm md:text-base" />
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">{t.projectSlugDescription}</p>
                </div>
                <div>
                  <Label htmlFor="description" className="text-sm md:text-base">{t.description}</Label>
                  <Input id="description" value={projectData.description} onChange={(e) => setProjectData({ ...projectData, description: e.target.value })} placeholder={t.descriptionPlaceholder} className="mt-2 text-sm md:text-base" disabled={saving} />
                </div>
                <div className="pt-4 border-t">
                  <Button onClick={handleSaveSettings} disabled={saving} className="gap-2 text-sm md:text-base">
                    {saving ? <>{t.saving}</> : <>{t.save}</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="widget" className="space-y-6">
            <DomainManagement tenantId={activeProject.id} />
          </TabsContent>

          <TabsContent value="active-projects" className="space-y-6">
            <ActiveProjectsTab t={t} />
          </TabsContent>

          <TabsContent value="pricing" className="space-y-4 md:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl">{t.accountPlan}</CardTitle>
                <CardDescription className="text-sm md:text-base">{t.accountPlanDescription}</CardDescription>
              </CardHeader>
              <CardContent className={showUpgradeModal ? 'opacity-50 pointer-events-none transition-opacity duration-300' : 'transition-opacity duration-300'}>
                <PricingPlans
                  onPlanSelect={handlePlanUpgradeClick}
                  currentPlan={accountPlan?.plan_type || 'free'}
                  currentBillingInterval={accountPlan?.billing_interval || 'month'}
                  showBillingToggle={true}
                  billingInterval={billingInterval}
                  onBillingIntervalChange={setBillingInterval}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Upgrade/Downgrade Modal */}
        {showUpgradeModal && (upgradePricing || upgradeLoading) && (
          <div className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md mx-auto">
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">{isDowngrade ? t.downgradeTitle : t.upgradeTitle}</CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {isDowngrade ? t.downgradeDescription : t.upgradeDescription}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-6">
                {upgradeLoading ? (
                  <div className="text-center py-6 md:py-8 text-gray-600 dark:text-gray-400 text-sm md:text-base">{t.loadingPrice}</div>
                ) : (
                  <>
                    {/* Current Plan */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 md:p-4 bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs md:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">{t.currentPlanLabel}</p>
                      <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white capitalize">
                        {getPlanName(upgradePricing?.current_plan)}
                      </p>
                      <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                        €{(upgradePricing?.current_price / 100).toFixed(2)}
                      </p>
                    </div>

                    {/* Arrow down */}
                    <div className="flex justify-center text-gray-400 dark:text-gray-500 text-lg md:text-xl">↓</div>

                    {/* New Plan */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20">
                      <p className="text-xs md:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">{t.newPlanLabel}</p>
                      <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white capitalize">
                        {getPlanName(upgradePricing?.new_plan)}
                      </p>
                      <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                        €{(upgradePricing?.new_price / 100).toFixed(2)}
                      </p>
                    </div>

                    {/* Warning or Price Difference */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 md:pt-4">
                      {isDowngrade ? (
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2 md:p-3 text-amber-800 dark:text-amber-300 text-xs md:text-sm">
                          {t.downgradeWarning.replace('{date}', upgradePricing?.current_period_end ? new Date(upgradePricing.current_period_end * 1000).toLocaleDateString() : new Date().toLocaleDateString())}
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300">{t.priceDifference}</span>
                            <span className="text-base md:text-lg font-bold text-cyan-600 dark:text-cyan-400">
                              €{(upgradePricing?.price_difference / 100).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-2 md:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <span className="text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200">{t.totalCharge}</span>
                            <span className="text-xl md:text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                              €{(upgradePricing?.price_difference / 100).toFixed(2)}
                            </span>
                          </div>
                          {upgradePricing?.current_plan !== 'free' && (
                            <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 mt-2">
                              {t.firstPaymentOnly.replace('{price}', (upgradePricing?.new_price / 100).toFixed(2))}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
              <div className="flex flex-col md:flex-row gap-2 md:gap-3 px-4 md:px-6 pb-4 md:pb-6">
                <Button
                  onClick={() => setShowUpgradeModal(false)}
                  variant="outline"
                  className="w-full text-xs md:text-sm"
                  disabled={saving}
                >
                  {t.cancel}
                </Button>
                <Button
                  onClick={confirmUpgrade}
                  className={`w-full text-xs md:text-sm ${isDowngrade ? 'bg-amber-600 hover:bg-amber-700' : 'bg-cyan-600 hover:bg-cyan-700'}`}
                  disabled={saving || upgradeLoading}
                >
                  {saving ? t.updating : (isDowngrade ? t.confirmDowngrade : t.continuePayment)}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Pending Scheduled Change Warning Modal */}
        {showPendingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm mx-auto">
              <CardHeader>
                <CardTitle className="text-amber-600 dark:text-amber-400 text-lg md:text-xl">{t.pendingDowngradeTitle || "Pending Plan Change"}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                  {(t.pendingDowngradeMessage || "Your change is scheduled for {date}. Please wait for the process to complete.")
                    .replace('{plan}', getPlanName(accountPlan.scheduled_change?.new_plan) || 'new plan')
                    .replace('{date}', new Date((accountPlan.scheduled_change?.date || 0) * 1000).toLocaleDateString())
                  }
                </p>
              </CardContent>
              <div className="flex justify-end p-3 md:p-4">
                <Button onClick={() => setShowPendingModal(false)} className="bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 text-xs md:text-sm">
                  {t.ok || "OK"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Enterprise Contact Modal - same SMTP as landing page contact */}
        <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">
                {tGlobal.contactModalTitle || 'Contact us'}
              </DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                {tGlobal.contactModalDescription || 'Send your request and we will get back to you. Same SMTP as landing contact form.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <Label htmlFor="contact-name" className="text-sm text-gray-900 dark:text-gray-100">{tGlobal.yourName || 'Your name'} *</Label>
                <Input
                  id="contact-name"
                  value={contactFormData.name}
                  onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                  placeholder={tGlobal.yourNamePlaceholder || 'John Doe'}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-sm text-gray-900 dark:text-gray-100">{tGlobal.email || 'Email'} *</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactFormData.email}
                  onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  placeholder={tGlobal.emailPlaceholder || 'you@company.com'}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="contact-subject" className="text-sm text-gray-900 dark:text-gray-100">{tGlobal.subject || 'Subject'} *</Label>
                <Input
                  id="contact-subject"
                  value={contactFormData.subject}
                  onChange={(e) => setContactFormData({ ...contactFormData, subject: e.target.value })}
                  placeholder={tGlobal.subjectPlaceholder || 'Subject'}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="contact-message" className="text-sm text-gray-900 dark:text-gray-100">{tGlobal.message || 'Message'} *</Label>
                <Textarea
                  id="contact-message"
                  value={contactFormData.message}
                  onChange={(e) => setContactFormData({ ...contactFormData, message: e.target.value })}
                  placeholder={tGlobal.messagePlaceholder || 'Your message...'}
                  required
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setShowContactModal(false)} disabled={contactLoading}>
                  {tGlobal.cancel || t.cancel || 'Cancel'}
                </Button>
                <Button type="submit" disabled={contactLoading} className="bg-cyan-600 hover:bg-cyan-700">
                  {contactLoading ? (tGlobal.sending || 'Sending...') : (tGlobal.sendMessage || 'Send message')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

// Active Projects Tab Component
const ActiveProjectsTab = ({ t }) => {
  const { projects } = useAuth();
  const [projectsData, setProjectsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activatingProjects, setActivatingProjects] = useState(new Set());

  useEffect(() => {
    loadActiveProjectsData();
  }, []);

  const loadActiveProjectsData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/projects/active-status');
      setProjectsData(response.data);
    } catch (error) {
      console.error('Failed to load active projects data:', error);
      toast.error(t.activationFailed || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProject = async (projectId, currentStatus) => {
    const newStatus = !currentStatus;
    const isActivating = newStatus;

    setActivatingProjects(new Set([...activatingProjects, projectId]));

    try {
      const endpoint = isActivating
        ? `/projects/${projectId}/activate`
        : `/projects/${projectId}/deactivate`;

      await api.put(endpoint);

      // Update local state
      if (projectsData) {
        const updatedProjects = projectsData.projects.map(p =>
          p.id === projectId ? { ...p, is_active: newStatus } : p
        );
        const newActiveCount = updatedProjects.filter(p => p.is_active).length;
        setProjectsData({
          ...projectsData,
          projects: updatedProjects,
          active_count: newActiveCount,
        });
      }

      toast.success(
        isActivating
          ? t.projectActivated || 'Project activated'
          : t.projectDeactivated || 'Project deactivated'
      );
    } catch (error) {
      let errorMessage = '';

      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Maximum active projects')) {
        // Max active projects reached - use translated message
        errorMessage = t.maxActiveProjectsReached || 'Maximum active projects reached';
      } else {
        // Use backend detail or fallback to translated message
        errorMessage = error.response?.data?.detail || (
          isActivating
            ? (t.activationFailed || 'Activation failed')
            : (t.deactivationFailed || 'Deactivation failed')
        );
      }

      toast.error(errorMessage);
    } finally {
      setActivatingProjects(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-600">
          {t.loading}
        </CardContent>
      </Card>
    );
  }

  if (!projectsData || projectsData.projects.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-600">
          {t.noProjects}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl md:text-2xl">{t.activeProjects || 'Active Projects'}</CardTitle>
        <CardDescription className="text-sm md:text-base">
          {t.activeProjectsDescription || 'Manage how many projects can be active based on your plan'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t.maxActiveProjects || 'Maximum Active Projects:'}
            </p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
              {projectsData.max_active_projects === null ? t.unlimited || '∞' : projectsData.max_active_projects}
            </p>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t.activeProjectsCount || 'Active Projects:'}
            </p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
              {projectsData.active_count}
            </p>
          </div>
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t.totalProjects || 'Total Projects:'}
            </p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
              {projectsData.total_projects}
            </p>
          </div>
        </div>

        {/* Projects List */}
        <div className="space-y-3">
          {projectsData.projects.map(project => (
            <div
              key={project.id}
              className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition"
            >
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">{project.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {project.allowed_domains.length} {project.allowed_domains.length === 1 ? 'domain' : 'domains'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    project.is_active
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                  }`}
                >
                  {project.is_active ? (t.active || 'Active') : (t.inactive || 'Inactive')}
                </span>
                <Button
                  size="sm"
                  variant={project.is_active ? 'outline' : 'default'}
                  onClick={() => handleToggleProject(project.id, project.is_active)}
                  disabled={activatingProjects.has(project.id)}
                  className="text-xs"
                >
                  {activatingProjects.has(project.id) ? (
                    project.is_active ? (t.deactivatingProject || 'Deactivating...') : (t.activatingProject || 'Activating...')
                  ) : (
                    project.is_active ? (t.deactivate || 'Deactivate') : (t.activate || 'Activate')
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
